import { yupResolver } from "@hookform/resolvers/yup";
import { toastServerError } from "helpers";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Input, Modal, Select } from "ui";

import { useUpsertMarketingTemplate } from "../../apis";
import { marketingTemplateSchema } from "../../helpers";
import {
  EMarketingTemplateCategory,
  MarketingTemplate
} from "../../marketing.types";

import styles from "./TemplateModal.module.scss";

const FORM_ID = "marketing-template-form";

interface TemplateFormValues {
  name: string;
  language: string;
  category: string;
  bodyText: string;
  variableCount: number;
}

interface TemplateModalProps {
  isOpen: boolean;
  editableTemplate: MarketingTemplate | null;
  onClose: () => void;
}

const LANGUAGE_OPTIONS = [
  { label: "Français", value: "fr" },
  { label: "English", value: "en" }
];

const CATEGORY_OPTIONS = [
  { label: "Marketing", value: EMarketingTemplateCategory.Marketing },
  { label: "Utility", value: EMarketingTemplateCategory.Utility }
];

const TemplateModal = ({
  isOpen,
  editableTemplate,
  onClose
}: TemplateModalProps) => {
  const { t } = useTranslation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<TemplateFormValues>({
    resolver: yupResolver(marketingTemplateSchema),
    defaultValues: {
      name: editableTemplate?.name ?? "",
      language: editableTemplate?.language ?? "fr",
      category: editableTemplate?.category ?? EMarketingTemplateCategory.Marketing,
      bodyText: editableTemplate?.bodyText ?? "",
      variableCount: editableTemplate?.variableCount ?? 0
    }
  });

  const { mutate: upsertTemplate, isLoading } = useUpsertMarketingTemplate({
    onSuccess: () => {
      toast.success(
        editableTemplate ? t("Template updated") : t("Template registered")
      );
      onClose();
    },
    onError: toastServerError
  });

  const handleFormSubmit = (values: TemplateFormValues) =>
    upsertTemplate({
      name: values.name,
      language: values.language,
      category: values.category as EMarketingTemplateCategory,
      bodyText: values.bodyText,
      variableCount: values.variableCount
    });

  return (
    <Modal
      isOpen={isOpen}
      title={editableTemplate ? t("Edit template") : t("Register template")}
      contentClassName={styles.modalContent}
      formId={FORM_ID}
      isLoading={isLoading}
      okText={editableTemplate ? t("Save") : t("Register")}
      onClose={onClose}
    >
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <Input
          label={t("Template name")}
          placeholder="welcome_offer_v1"
          disabled={Boolean(editableTemplate)}
          errorMessage={errors.name?.message}
          {...register("name")}
        />

        <div className={styles.form__row}>
          <Controller
            control={control}
            name="language"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("Language")}
                options={LANGUAGE_OPTIONS}
                value={value}
                onChange={onChange}
                errorMessage={errors.language?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="category"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("Category")}
                options={CATEGORY_OPTIONS}
                value={value}
                onChange={onChange}
                errorMessage={errors.category?.message}
              />
            )}
          />
        </div>

        <Input
          label={t("Message body")}
          asTag="textarea"
          placeholder={t("Salut {{1}}, -10% on your first order...")}
          errorMessage={errors.bodyText?.message}
          {...register("bodyText")}
        />

        <Input
          label={t("Variable count")}
          type="number"
          onlyInteger
          errorMessage={errors.variableCount?.message}
          {...register("variableCount")}
        />

        <span className={styles.form__hint}>
          {t(
            "Templates are created and approved in Meta. This registers the local record; run Sync to pull the approval status."
          )}
        </span>
      </form>
    </Modal>
  );
};

export default TemplateModal;
